import { create } from 'zustand';
import { isEqual } from 'lodash';
import type { FilterCondition } from '@src/shared/types/user-journeys';

export const DEFAULT_TOP_PATHS = 10;

export enum DisplayType {
  PATTERNS = 'patterns',
  SANKEY = 'sankey',
}

// Group all configurable fields into a single interface
export interface JourneyConfig {
  businessGoal: string;
  startEvents: string[];
  includeEvents: string[];
  excludeEvents: string[];
  hiddenEvents: string[];
  includeMatchType: 'every' | 'any';
  topPaths: number;
  timeout: number;
  timeoutUnit: string;
}

export const DEFAULT_CONFIG: JourneyConfig = {
  businessGoal: '',
  startEvents: [],
  includeEvents: [],
  excludeEvents: [],
  hiddenEvents: [],
  includeMatchType: 'any',
  topPaths: DEFAULT_TOP_PATHS,
  timeout: 1,
  timeoutUnit: 'h',
};

interface IJourneyStore {
  // Applied config (used for queries)
  applied: JourneyConfig;
  // Draft config (user edits before clicking Apply)
  draft: JourneyConfig;

  // Update draft config (partial updates supported)
  updateDraft: (partial: Partial<JourneyConfig>) => void;
  // Apply draft to applied (copies draft → applied)
  applyConfig: () => void;
  // Discard draft changes (resets draft → applied)
  discardChanges: () => void;
  // Check if there are unsaved changes
  hasChanges: () => boolean;

  // Non-configurable state (immediate effect, no Apply needed)
  version?: string;
  setVersion: (version: IJourneyStore['version']) => void;
  flows: any[];
  setFlows: (flows: IJourneyStore['flows']) => void;
  selectedNodes: string[];
  setSelectedNodes: (selectedNode: string[]) => void;
  globalFilter?: FilterCondition[][];
  setGlobalFilter: (filter: FilterCondition[][]) => void;
  time: any;
  setTime: (time: any) => void;
  showGenerateJourneyModal: boolean;
  setShowGenerateJourneyModal: (showGenerateJourneyModal: boolean) => void;
  displayType: DisplayType;
  setDisplayType: (displayType: DisplayType) => void;
  interval: [string, string] | undefined;
  setInterval: (interval: [string, string] | undefined) => void;

  // Reset everything
  resetAllData: () => void;

  // Legacy compatibility - these now update draft directly
  // (Can be removed once all usages migrate to updateDraft)
  setDraftBusinessGoal: (v: string) => void;
  setDraftStartEvents: (v: string[]) => void;
  setDraftIncludeEvents: (v: string[]) => void;
  setDraftExcludeEvents: (v: string[]) => void;
  setDraftHiddenEvents: (v: string[]) => void;
  setDraftIncludeMatchType: (v: 'every' | 'any') => void;
  setDraftTopPaths: (v: number) => void;
  setDraftTimeoutValue: (v: number) => void;
  setDraftTimeoutUnit: (v: string) => void;
}

export const useJourneyStore = create<IJourneyStore>((set, get) => ({
  // Config state
  applied: { ...DEFAULT_CONFIG },
  draft: { ...DEFAULT_CONFIG },

  updateDraft: (partial) =>
    set((state) => ({
      draft: { ...state.draft, ...partial },
    })),

  applyConfig: () =>
    set((state) => ({
      applied: { ...state.draft },
    })),

  discardChanges: () =>
    set((state) => ({
      draft: { ...state.applied },
    })),

  hasChanges: () => {
    const { applied, draft } = get();
    return !isEqual(applied, draft);
  },

  // Non-configurable state
  version: undefined,
  setVersion: (version) => set({ version }),

  flows: [],
  setFlows: (flows) => set({ flows }),

  selectedNodes: [],
  setSelectedNodes: (selectedNodes) => set({ selectedNodes }),

  globalFilter: undefined,
  setGlobalFilter: (globalFilter) => set({ globalFilter }),

  time: undefined,
  setTime: (time) => set({ time }),

  showGenerateJourneyModal: true,
  setShowGenerateJourneyModal: (showGenerateJourneyModal) => set({ showGenerateJourneyModal }),

  displayType: DisplayType.SANKEY,
  setDisplayType: (displayType) => set({ displayType }),

  interval: undefined,
  setInterval: (interval) => set({ interval }),

  resetAllData: () =>
    set({
      applied: { ...DEFAULT_CONFIG },
      draft: { ...DEFAULT_CONFIG },
      flows: [],
      selectedNodes: [],
      globalFilter: undefined,
      time: undefined,
      displayType: DisplayType.SANKEY,
      interval: undefined,
    }),

  // Legacy compatibility setters (shorthand for updateDraft)
  setDraftBusinessGoal: (v) => set((s) => ({ draft: { ...s.draft, businessGoal: v } })),
  setDraftStartEvents: (v) => set((s) => ({ draft: { ...s.draft, startEvents: v } })),
  setDraftIncludeEvents: (v) => set((s) => ({ draft: { ...s.draft, includeEvents: v } })),
  setDraftExcludeEvents: (v) => set((s) => ({ draft: { ...s.draft, excludeEvents: v } })),
  setDraftHiddenEvents: (v) => set((s) => ({ draft: { ...s.draft, hiddenEvents: v } })),
  setDraftIncludeMatchType: (v) => set((s) => ({ draft: { ...s.draft, includeMatchType: v } })),
  setDraftTopPaths: (v) => set((s) => ({ draft: { ...s.draft, topPaths: v } })),
  setDraftTimeoutValue: (v) => set((s) => ({ draft: { ...s.draft, timeout: v } })),
  setDraftTimeoutUnit: (v) => set((s) => ({ draft: { ...s.draft, timeoutUnit: v } })),
}));

// Convenience selectors for cleaner component code
export const useAppliedConfig = () => useJourneyStore((s) => s.applied);
export const useDraftConfig = () => useJourneyStore((s) => s.draft);
export const useHasChanges = () => useJourneyStore((s) => s.hasChanges());
